import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import CorrectChoice from "../../Assets/final new sound/correct-2-46134.mp3";
import incorrectSound from "../../Assets/final new sound/invalid-selection-39351.mp3";
import { server_url } from "../../Config/API";
import AuthUser from "../../Hooks/authUser";
import PostHooks from "../../Hooks/PostHooks";
import UpdateHooks from "../../Hooks/UpdateHooks";
import GameStatsIndicator from "./GameStatsIndicator";

function SmoothMovement() {
  const navigate = useNavigate();
  const generateRandomLetter = () => {
    const randomIndex = Math.floor(Math.random() * 26);
    return String.fromCharCode(65 + randomIndex);
  };

  const [movingLetter, setMovingLetter] = useState(generateRandomLetter());
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [gameActive, setGameActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState(300);
  const [score, setScore] = useState(0);
  const [smoothData, setSmoothData] = useState([]);

  const correctAudioRef = useRef(null);
  const incorrectAudioRef = useRef(null);
  if (typeof window !== "undefined") {
    if (!correctAudioRef.current) correctAudioRef.current = new Audio(CorrectChoice);
    if (!incorrectAudioRef.current) incorrectAudioRef.current = new Audio(incorrectSound);
  }

  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const movingLetterRef = useRef(movingLetter);
  const gameActiveRef = useRef(gameActive);

  // Direction Refs to avoid clearing the movement interval on boundary hits
  const xDirRef = useRef(1);
  const yDirRef = useRef(1);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  movingLetterRef.current = movingLetter;
  gameActiveRef.current = gameActive;

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/smoothMovement/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Smooth+Movement+Monocular`
      );
      const data = await response.json();
      setSmoothData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching smooth movement data:", error);
    }
  };

  useEffect(() => {
    if (smoothData?.length) {
      setTimeLeft(smoothData[0]?.remainingTime);
      setScore(smoothData[0]?.score);
    }
  }, [smoothData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/smoothMovement/updateSmoothMovement/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/smoothMovement/addSmoothMovement`,
        {
          gameName: "Smooth Movement Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Smooth movement score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    enterFullscreen();
  }, []);

  useEffect(() => {
    if (gameActive) {
      const moveInterval = setInterval(() => {
        setPosition((prevPosition) => {
          const newX = prevPosition.x + xDirRef.current * 2;
          const newY = prevPosition.y + yDirRef.current * 2;

          if (newX <= 0 || newX >= window.innerWidth) {
            xDirRef.current = -xDirRef.current;
          }
          if (newY <= 0 || newY >= window.innerHeight) {
            yDirRef.current = -yDirRef.current;
          }

          return { x: newX, y: newY };
        });
      }, 30);

      return () => clearInterval(moveInterval);
    }
  }, [gameActive]);

  const handleKeyDown = (event) => {
    if (gameActive && event.keyCode >= 65 && event.keyCode <= 90) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleKeyPress = useCallback((event) => {
    if (gameActiveRef.current) {
      const pressedKey = event.key.toUpperCase();
      if (pressedKey === movingLetterRef.current) {
        if (correctAudioRef.current) {
          correctAudioRef.current.currentTime = 0;
          correctAudioRef.current.play().catch(e => console.log(e));
        }
        setScore((prevCount) => prevCount + 1);
        setMovingLetter(generateRandomLetter());
      } else {
        if (event.keyCode !== 27) {
          if (incorrectAudioRef.current) {
            incorrectAudioRef.current.currentTime = 0;
            incorrectAudioRef.current.play().catch(e => console.log(e));
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          setGameActive(false);
          insertFunctionRef.current();
          navigate(-1);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const handleEscKey = async (event) => {
      if (event.keyCode === 27) {
        insertFunctionRef.current();
        window.history.back();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  const textStyle = {
    fontSize: "85px",
  };
  const min = Math.floor(timeLeft / 60);
  return (
    <div className="flex fixed inset-0 bg-white items-center justify-center min-h-screen">
      {gameActive && (
        <div
          className="absolute text-2xl cursor-pointer transition-transform duration-500"
          style={{
            left: position.x + "px",
            top: position.y + "px",
            ...textStyle,
          }}
        >
          {movingLetter}
        </div>
      )}
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
    </div>
  );
}

export default SmoothMovement;
